import { daysBetween } from '~/calculations/calendar'
import { calculateWeightTrend } from '~/calculations/weightTrends'
import type { CsvZipFile } from '~/exports/csv/csvTables'
import { serializeWeeklyReport } from '~/exports/json/weeklyReport'
import { buildMediaZip, type MediaSource, type MediaZipFile } from '~/exports/media/mediaZip'
import type { PdfThumbnail } from '~/exports/pdf/documentModel'
import type { ReportModel, ReviewWithOpinion } from '~/exports/reportModel'
import { isInPeriod, type PrivacySelection, reportingPeriodFor } from '~/exports/scope'
import { evaluateWeek } from '~/rules/engine'
import { DEFAULT_RULES_THRESHOLDS } from '~/rules/thresholds'
import { getBlob } from '~/storage/blobStore'
import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import { getActiveGoal } from '~/storage/repositories/goalRepository'
import { listMeasurementSessions } from '~/storage/repositories/measurementRepository'
import { getOpinion } from '~/storage/repositories/opinionRepository'
import { getActivePhase, listPhases } from '~/storage/repositories/phaseRepository'
import { listPhotos } from '~/storage/repositories/photoRepository'
import { getProfile } from '~/storage/repositories/profileRepository'
import { getActiveProject } from '~/storage/repositories/projectRepository'
import { getSettings } from '~/storage/repositories/settingsRepository'
import { listStrengthEntries } from '~/storage/repositories/strengthEntryRepository'
import { findTrainingWeek } from '~/storage/repositories/trainingWeekRepository'
import { listWeeklyReviews } from '~/storage/repositories/weeklyReviewRepository'
import { listWeightEntriesInRange } from '~/storage/repositories/weightRepository'
import { toIsoTimestamp } from '~/types/dates'
import { err, ok, type Result } from '~/utils/result'
import { getProjection } from './projectionService'
import { collectRulesInput } from './rulesService'
import { getLiftSummaries } from './strengthService'
import { todayFor } from './weightService'

/**
 * Build and deliver the weekly AI-review export.
 *
 * The order here matters and is not incidental: the report model is assembled
 * with redaction already applied, and only then handed to a renderer. A
 * renderer cannot leak an excluded value because it never receives one
 * (implementation-plan.md §8).
 */

export type ExportFailure =
  | StorageError
  | { readonly kind: 'exportPrecondition'; readonly message: string }

export async function buildWeeklyReportModel(
  db: SolaDatabase,
  now: Date,
  selection: PrivacySelection,
): Promise<Result<ReportModel, ExportFailure>> {
  const reference = todayFor(now)

  const profile = await getProfile(db)
  if (!profile.ok) return profile
  if (profile.value === undefined) {
    return err({
      kind: 'exportPrecondition',
      message: 'Project Sola needs your profile before it can build a report. Finish setup first.',
    })
  }

  const settings = await getSettings(db, now)
  if (!settings.ok) return settings

  const period = reportingPeriodFor(reference, selection, settings.value.weekStartDay)

  /* Full history has no lower bound; the repository needs one, so the earliest
     representable date stands in for "from the beginning". */
  const from = period.from ?? EARLIEST_DATE
  const entries = await listWeightEntriesInRange(db, from, period.to)
  if (!entries.ok) return entries

  const sessions = await listMeasurementSessions(db)
  if (!sessions.ok) return sessions

  const phase = await getActivePhase(db)
  if (!phase.ok) return phase

  const goal = await getActiveGoal(db)
  if (!goal.ok) return goal

  const project = await getActiveProject(db)
  if (!project.ok) return project

  /* The opinion is computed from the SAME trailing-window snapshot the Review
     screen shows, not from the exported period, so the exported opinion is the
     one the user actually saw. The export period governs which observations are
     listed, not how the week was judged. */
  const snapshot = await collectRulesInput(db, now)
  if (!snapshot.ok) return snapshot

  /* Finalized reviews carry the opinion they were GIVEN, which is not the same
     as the opinion the engine would produce now — that is the whole point of
     D-046's immutable snapshot. Nothing here recomputes a past week. */
  const reviews = await listWeeklyReviews(db)
  if (!reviews.ok) return reviews

  const finalized = reviews.value
    .filter((review) => review.status === 'complete' && isInPeriod(review.weekStartDate, period))
    .sort((left, right) => (left.weekStartDate as string).localeCompare(right.weekStartDate))

  const weeklyReviews: ReviewWithOpinion[] = []
  for (const review of finalized) {
    const opinion =
      review.opinionId === undefined ? undefined : await getOpinion(db, review.opinionId)
    /* Read by the review's own project and week rather than by the current one:
       a report can span several weeks and, after D-103, more than one phase of
       one project. A review with no `projectId` predates D-062's project layer,
       so there is nothing to match a week against. */
    const trainingWeek =
      review.projectId === undefined
        ? undefined
        : await findTrainingWeek(db, review.projectId, review.weekStartDate)
    weeklyReviews.push({
      review,
      opinion: opinion !== undefined && opinion.ok ? opinion.value : undefined,
      ...(trainingWeek !== undefined && trainingWeek.ok && trainingWeek.value !== undefined
        ? { trainingWeek: trainingWeek.value }
        : {}),
    })
  }

  const lifts = await getLiftSummaries(db, now)
  if (!lifts.ok) return lifts

  const sets = await listStrengthEntries(db)
  if (!sets.ok) return sets

  const projection = await getProjection(db, now)
  if (!projection.ok) return projection

  const phases = await listPhases(db)
  if (!phases.ok) return phases

  /* Metadata only — no blob is read here. The bytes are fetched by the media
     ZIP path, and only when the user asked for one. */
  const photos = await listPhotos(db)
  if (!photos.ok) return photos

  return ok({
    photos: photos.value.filter((photo) => isInPeriod(photo.capturedDate, period)),
    weeklyReviews,
    liftSummaries: lifts.value.map(({ lift, summary }) => ({ lift, summary })),
    strengthEntries: sets.value.filter((entry) => isInPeriod(entry.date, period)),
    projection: projection.value,
    phases: phases.value,
    generatedAt: toIsoTimestamp(now),
    reference,
    period,
    selection,
    profile: profile.value,
    project: project.value,
    phase: phase.value,
    goal: goal.value,
    weightEntries: entries.value,
    weightTrend: calculateWeightTrend(
      entries.value.map((entry) => ({ date: entry.date, weightKg: entry.weightKg })),
      reference,
      DEFAULT_RULES_THRESHOLDS.sufficiency,
    ),
    measurementSessions: sessions.value.filter((session) => isInPeriod(session.date, period)),
    outcome: evaluateWeek(snapshot.value),
  })
}

/**
 * The tabular export (D-082).
 *
 * Built from the same serialized document as the JSON and the PDF, so the three
 * artifacts cannot disagree and redaction is applied once. Lazily imported: a
 * ZIP writer and the flattening code are dead weight for the common path, which
 * is a JSON file.
 */
export async function buildWeeklyCsvZip(
  model: ReportModel,
): Promise<Result<CsvZipFile, ExportFailure>> {
  const { buildCsvZip } = await import('~/exports/csv/csvTables')
  const zipFile = await buildCsvZip(serializeWeeklyReport(model), model.reference as string)

  if (zipFile.tables.length === 0) {
    return err({
      kind: 'exportPrecondition',
      message:
        'There is nothing to put in a spreadsheet yet — no weigh-ins, measurements or reviews ' +
        'in the period this export covers.',
    })
  }

  return ok(zipFile)
}

export async function deliverCsvZip(file: CsvZipFile): Promise<void> {
  return deliverFile(
    new File([file.bytes as BlobPart], file.fileName, { type: 'application/zip' }),
    'Project Sola data',
  )
}

/**
 * Thumbnails for the PDF, read only when the user opted into photos.
 *
 * **The derived ~320 px thumbnail, never the stored image** — that is what
 * `privacy-and-storage.md` restricts a PDF to, and D-081 stored it partly for
 * this. A missing thumbnail is skipped rather than failing the export: it is
 * regenerable and never authoritative, so losing one is cosmetic, and refusing
 * a whole report over a cosmetic gap would be the wrong trade.
 */
async function loadThumbnails(
  db: SolaDatabase,
  model: ReportModel,
): Promise<Result<PdfThumbnail[], ExportFailure>> {
  if (!model.selection.includePhotos) return ok([])

  const thumbnails: PdfThumbnail[] = []

  for (const photo of model.photos) {
    const stored = await getBlob(db, photo.thumbnailBlobId)
    if (!stored.ok) return stored
    if (stored.value === undefined) continue

    thumbnails.push({
      dataUri: await toDataUri(stored.value.blob),
      /* The caption follows the same gating as everything else: a date appears
         only if the user included exact dates. */
      caption: model.selection.includeExactDates
        ? `${photo.view} · ${photo.capturedDate}`
        : photo.view,
      widthPx: photo.widthPx,
      heightPx: photo.heightPx,
    })
  }

  return ok(thumbnails)
}

function toDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('The thumbnail could not be read.'))
    reader.readAsDataURL(blob)
  })
}

/**
 * The optional media ZIP (D-082).
 *
 * **Refuses rather than half-delivers.** If the user opted out of photos there
 * is nothing to build, and if a blob cannot be read the ZIP is not produced —
 * silently shipping a manifest that lists a file the archive does not contain
 * would make the manifest a lie, and the manifest is the only thing that makes
 * the archive readable.
 *
 * Blobs are read **one at a time** and only the stored image, never the
 * thumbnail. Peak memory is bounded by the archive itself plus the largest
 * single photo, which is the same bound `implementation-plan.md` §6 sets for a
 * backup and the same one device check 39 exists to confirm.
 */
export async function buildWeeklyMediaZip(
  db: SolaDatabase,
  model: ReportModel,
): Promise<Result<MediaZipFile, ExportFailure>> {
  if (!model.selection.includePhotos) {
    return err({
      kind: 'exportPrecondition',
      message: 'Photos were not included in this export, so there is no media file to create.',
    })
  }

  if (model.photos.length === 0) {
    return err({
      kind: 'exportPrecondition',
      message: 'There are no photos in the period this export covers.',
    })
  }

  const earliest = earliestObservationDate(model)
  const sources: MediaSource[] = []

  for (const photo of model.photos) {
    const stored = await getBlob(db, photo.blobId)
    if (!stored.ok) return stored
    if (stored.value === undefined) {
      return err({
        kind: 'exportPrecondition',
        message:
          'One of the photos could not be read from storage, so the media file was not created. ' +
          'Nothing was changed.',
      })
    }

    sources.push({
      photo,
      bytes: new Uint8Array(await stored.value.blob.arrayBuffer()),
      dayIndex: earliest === undefined ? 0 : daysBetween(earliest, photo.capturedDate),
    })
  }

  return ok(await buildMediaZip(model, sources))
}

/** Deliver the media ZIP down the shared share-then-download path. */
export async function deliverMediaZip(file: MediaZipFile): Promise<void> {
  return deliverFile(
    new File([file.bytes as BlobPart], file.fileName, { type: 'application/zip' }),
    'Project Sola photos',
  )
}

/**
 * The anchor `dayIndex` counts from, matching the JSON document exactly.
 *
 * Anchored to the data rather than to the period start, so it is defined for a
 * full-history export too, which has no lower bound.
 */
function earliestObservationDate(model: ReportModel): ReportModel['reference'] | undefined {
  const dates = [
    ...model.weightEntries.map((entry) => entry.date),
    ...model.measurementSessions.map((session) => session.date),
  ]
  return dates.length === 0 ? undefined : dates.reduce((a, b) => (a <= b ? a : b))
}

/** The user dismissed the share sheet, which browsers report as `AbortError`. */
function isShareCancelled(cause: unknown): boolean {
  return cause instanceof Error && cause.name === 'AbortError'
}

/** Earliest date the calendar type accepts; stands in for "no lower bound". */
const EARLIEST_DATE = '0001-01-01' as ReportModel['reference']

export interface WeeklyReportFile {
  readonly fileName: string
  readonly json: string
}

export function renderWeeklyReport(model: ReportModel): WeeklyReportFile {
  const document = serializeWeeklyReport(model)
  return {
    fileName: `sola-weekly-${model.reference}.json`,
    // Indented: the user may well open this before sending it on.
    json: JSON.stringify(document, null, 2),
  }
}

export interface WeeklyPdfFile {
  readonly fileName: string
  readonly blob: Blob
}

/**
 * Fetch the PDF chunks before the user asks for a file.
 *
 * **This is about the Share sheet, not about speed.** `navigator.share` requires
 * transient user activation, and every `await` between the tap and the call eats
 * into it — iOS Safari is the strictest implementation of that rule. Loading
 * 122 kB of `jspdf` inside the handler is exactly the kind of gap that costs the
 * activation, and losing it drops delivery to a download link, which on iOS
 * means no Files integration.
 *
 * Called when the user picks PDF, so by the time they confirm, the import
 * resolves in a microtask. **Failure is deliberately silent**: this is a
 * warm-up, and the real path reports its own errors.
 */
export function preloadPdfRenderer(): void {
  void import('~/exports/pdf/documentModel').catch(() => undefined)
  void import('~/exports/pdf/renderWeeklyPdf').catch(() => undefined)
}

/**
 * The same report, drawn (D-082).
 *
 * **Both documents come from one `serializeWeeklyReport` call.** The PDF is
 * built from the serialized JSON rather than from the model, so it cannot show a
 * value the JSON did not produce and cannot leak one the JSON redacted — the
 * agreement `implementation-plan.md` §5 requires is structural rather than
 * maintained.
 *
 * `jspdf` loads only when this is called (D-085).
 */
export async function renderWeeklyPdfReport(
  db: SolaDatabase,
  model: ReportModel,
): Promise<Result<WeeklyPdfFile, ExportFailure>> {
  const { buildPdfDocument } = await import('~/exports/pdf/documentModel')
  const { renderWeeklyPdf } = await import('~/exports/pdf/renderWeeklyPdf')

  const thumbnails = await loadThumbnails(db, model)
  if (!thumbnails.ok) return thumbnails

  const drawn = await renderWeeklyPdf(
    buildPdfDocument(serializeWeeklyReport(model), thumbnails.value),
  )

  if (!(drawn instanceof Blob)) {
    const megabytes = Math.round(drawn.limitBytes / (1024 * 1024))
    return err({
      kind: 'exportPrecondition',
      message:
        `The PDF came out larger than ${megabytes} MB, which many readers refuse to open. ` +
        'Try a shorter period, or leave photos out.',
    })
  }

  return ok({ fileName: `sola-weekly-${model.reference}.pdf`, blob: drawn })
}

/**
 * Hand the file to the user.
 *
 * Web Share first, because that is how a file reaches Files on iOS, with a
 * download link as the fallback. Same shape as the backup delivery path.
 */
export async function deliverWeeklyReport(file: WeeklyReportFile): Promise<void> {
  return deliverFile(
    new File([file.json], file.fileName, { type: 'application/json' }),
    'Project Sola weekly report',
  )
}

/** The PDF down the same path, so cancellation and fallback behave identically. */
export async function deliverWeeklyPdf(file: WeeklyPdfFile): Promise<void> {
  return deliverFile(
    new File([file.blob], file.fileName, { type: 'application/pdf' }),
    'Project Sola weekly report',
  )
}

async function deliverFile(blob: File, title: string): Promise<void> {
  const fileName = blob.name

  if (typeof navigator !== 'undefined' && typeof navigator.canShare === 'function') {
    if (navigator.canShare({ files: [blob] })) {
      try {
        await navigator.share({ files: [blob], title })
        return
      } catch (cause: unknown) {
        /* Two very different situations, and reporting either as a failure
           would be wrong.

           The user dismissing the share sheet is a decision, not an error:
           nothing is broken and there is nothing to tell them. Anything else —
           `canShare` said yes but the platform could not follow through, which
           desktop browsers do — falls through to the download link, because the
           file exists and the user asked for it. */
        if (isShareCancelled(cause)) return
      }
    }
  }

  const url = URL.createObjectURL(blob)
  try {
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.click()
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }
}
